import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Nest } from '@captain-tasko/shared';
import { apiList } from '../lib/api.js';
import { useSession } from '../hooks/useSession.js';
import { OrgSwitcher } from './OrgSwitcher.js';
import { CaptainTaskoLogo } from './CaptainTaskoLogo.js';
import { AppFooter } from './AppFooter.js';
import { CopyShortUrlButton } from './CopyShortUrlButton.js';
// CTSK-493 Phase 1b — header document search, mounted only when the
// document-search flag is on; otherwise the pre-search disabled
// placeholder stays exactly as it was. CTSK-494 Phase 1c: the flag is the
// API's runtime value from GET /meta/features (`useFeatures()`), with the
// Vite build-time mirror as the fallback for one release. The `/`
// shortcut lives inside DocumentSearch, so (un)mounting here is what
// registers/unregisters it — no separate gate needed.
import { DocumentSearch } from './DocumentSearch/index.js';
import { useFeatures } from '../lib/features.js';
import { useNotesAccess } from '../hooks/useNotesAccess.js';
// CTSK-347 — tile deep-link resolution moved behind the shared
// resolver (precedence: landingPath → native page → filtered
// Settings → Tools). NATIVE_TOOL_TYPES is consumed inside it.
import {
  resolveToolLandingUrl,
  defaultToolsSettingsPath,
  isExternalToolUrl,
} from '../lib/tool-navigation.js';
import { nestTileDocsPath } from '../lib/native-nest-types.js';

interface NavItem {
  label: string;
  path: string;
  /** CTSK-522 — draw an unlabelled group separator above this item. */
  groupBreakBefore?: boolean;
}
interface NavSection { heading: string; items: NavItem[]; }

// Top sections — render above the dynamic Nests block.
const TOP_NAV_SECTIONS: NavSection[] = [
  {
    heading: 'Knowledge',
    items: [
      { label: 'Dashboard', path: '/' },
      { label: 'Documents', path: '/documents' },
      // CTSK-509 — hidden unless Notes is on for this org (see useNotesAccess).
      { label: 'Notes', path: '/notes' },
      { label: 'Storage', path: '/storage', groupBreakBefore: true },
      { label: 'File tasks', path: '/tasks/files' },
      { label: 'Tags', path: '/tags' },
      { label: 'Tagging rules', path: '/tags/rules' },
      { label: 'File workflows', path: '/file-workflows' },
      { label: 'Custom properties', path: '/custom-properties' },
    ],
  },
  {
    heading: 'Work',
    items: [
      { label: 'Projects', path: '/projects' },
      { label: 'Work items', path: '/workitems' },
      { label: 'Board', path: '/workitems/board' },
      // ctsk-073 — unified Watching panel (documents + work items).
      // Lives under Work because that's where the user is when they
      // ask "what's on my plate"; the page itself covers both
      // resources via tabs.
      { label: 'Watching', path: '/watching' },
    ],
  },
];

// Bottom sections — render BELOW the dynamic Nests block per
// dr.the0 (2026-05-30). System / Settings is conceptually a different
// surface from the navigation above and sits last so the operator's
// muscle-memory matches "everything operational on top, configuration
// at the bottom". Rendered regardless of whether the org has nests.
const BOTTOM_NAV_SECTIONS: NavSection[] = [
  {
    heading: 'System',
    items: [
      { label: 'Settings', path: '/settings' },
    ],
  },
];

export interface AppShellProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  children: ReactNode;
}

const ROCKER_DEFAULT_TOP = 140;
const ROCKER_HEIGHT = 46;
const NAV_COLLAPSED_KEY = 'tasko.shell.nav-collapsed.v1';

export function AppShell({ currentPath, onNavigate, children }: AppShellProps) {
  const { session, logout } = useSession();
  const featureFlags = useFeatures();
  // CTSK-509 — the Notes sidebar entry exists only while Notes is on for this org.
  const notesAccess = useNotesAccess();
  const topSections = TOP_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.path !== '/notes' || notesAccess.enabled),
  }));
  // ctsk-056 — sidebar shortcut row. Fetch the nests for the active
  // org so each gets a one-click shortcut. Cached for 60s like the
  // chip; org switches invalidate via the query key (session changes).
  const nestsQ = useQuery({
    queryKey: ['nests', session?.activeOrg?.slug],
    queryFn: () => apiList<Nest>('/nests'),
    enabled: !!session,
    staleTime: 60_000,
  });
  const nests = nestsQ.data?.data ?? [];
  // Per-nest stats (formerly a Dashboard section — moved here on
  // dr.the0's ask). Hidden under the section heading until hover so
  // the sidebar stays compact for non-nest navigation.
  const nestStatsQ = useQuery({
    queryKey: ['stats', 'nests', session?.activeOrg?.slug],
    queryFn: () =>
      apiList<{
        slug: string;
        code: string;
        name: string;
        // CTSK-384 — bundle-declared nest-tile landing destination;
        // wins over the hardcoded nestTileDocsPath routing. Omitted
        // when the nest row has no landing_path.
        landingPath?: string;
        rulesCount: number;
        fileWorkflowsCount: number;
        documentsCount: number;
        workItemsCount: number;
        // 0.1.8 — installed tools per nest. Count + per-tool metadata
        // so the sidebar can route directly to a single tool's
        // native page when only one is installed. CTSK-347 —
        // landingPath is the manifest-declared click destination.
        toolsCount: number;
        tools: Array<{ id: string; slug: string; type: string; landingPath?: string }>;
      }>('/stats/nests'),
    enabled: !!session && nests.length > 0,
    staleTime: 60_000,
  });
  const nestStatsBySlug = new Map(
    (nestStatsQ.data?.data ?? []).map((s) => [s.slug, s]),
  );
  // Left-nav drawer: pinned open/closed (saved), plus a hover "peek" from the left edge.
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try { return window.localStorage.getItem(NAV_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const [navPeek, setNavPeek] = useState(false);
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (peekTimer.current) clearTimeout(peekTimer.current); }, []);
  function toggleNav() {
    const next = !navCollapsed;
    setNavCollapsed(next);
    setNavPeek(false);
    try { window.localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* session-only */ }
  }
  // Rocker tab rides at the level of the page's [data-rocker-anchor] row (e.g. the
  // Documents "Select" row); pages without one use the default. Clamped on screen.
  const [rockerTop, setRockerTop] = useState(ROCKER_DEFAULT_TOP);
  useEffect(() => {
    let raf = 0;
    const place = () => {
      raf = 0;
      const el = document.querySelector('[data-rocker-anchor]');
      const r = el?.getBoundingClientRect();
      const mid = r ? r.top + r.height / 2 - ROCKER_HEIGHT / 2 : ROCKER_DEFAULT_TOP;
      setRockerTop(Math.round(Math.max(64, Math.min(window.innerHeight - ROCKER_HEIGHT - 48, mid))));
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(place); };
    schedule();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    // DOM changes (data arriving, filters toggling) are debounced so layout reads stay rare.
    let moTimer: ReturnType<typeof setTimeout> | null = null;
    const mo = new MutationObserver(() => { if (moTimer) clearTimeout(moTimer); moTimer = setTimeout(schedule, 150); });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      mo.disconnect();
      if (moTimer) clearTimeout(moTimer);
    };
  }, [currentPath]);
  const navHidden = navCollapsed && !navPeek;
  return (
    <div className={`shell${navCollapsed ? ' shell--nav-collapsed' : ''}`}>
      <header className="shell__top">
        {/* Brand mark — small ship's-wheel logo + wordmark. Clicking
            takes the user home, matching the convention of every
            other app's brand corner. */}
        <button
          type="button"
          onClick={() => onNavigate('/')}
          title="Captain Tasko — home"
          aria-label="Captain Tasko home"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          <CaptainTaskoLogo size={36} />
          <strong style={{ fontSize: 16 }}>Captain Tasko</strong>
        </button>
        {featureFlags.documentSearch ? (
          <DocumentSearch currentPath={currentPath} onNavigate={onNavigate} />
        ) : (
          <input placeholder="Quick search (coming in Phase 5)" disabled />
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <OrgSwitcher activeOrgSlug={session?.activeOrg?.slug ?? null} onNavigate={onNavigate} />
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{session?.user.displayName}</span>
          <button className="btn btn--ghost" onClick={logout}>Log out</button>
        </div>
      </header>
      {navCollapsed && (
        <div
          className="shell__edge"
          data-testid="nav-edge"
          onMouseEnter={() => { peekTimer.current = setTimeout(() => setNavPeek(true), 120); }}
          onMouseLeave={() => { if (peekTimer.current) clearTimeout(peekTimer.current); }}
        />
      )}
      <button
        type="button"
        className="shell__rocker"
        data-collapsed={navCollapsed ? 'true' : 'false'}
        aria-label={navCollapsed ? 'Open navigation' : 'Close navigation'}
        aria-expanded={!navCollapsed}
        title={navCollapsed ? 'Open navigation' : 'Close navigation'}
        onClick={toggleNav}
        style={{ top: rockerTop }}
      >
        {navCollapsed ? '›' : '‹'}
      </button>
      <nav
        className={`shell__nav${navPeek ? ' shell__nav--peek' : ''}`}
        aria-label="Primary"
        aria-hidden={navHidden || undefined}
        {...(navHidden ? ({ inert: '' } as object) : {})}
        onMouseLeave={() => setNavPeek(false)}
        onKeyDown={(e) => { if (e.key === 'Escape') setNavPeek(false); }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {topSections.map((section, idx) => (
            <NavSectionView
              key={section.heading}
              section={section}
              showSeparator={idx > 0}
              currentPath={currentPath}
              onNavigate={onNavigate}
            />
          ))}

          {/* ctsk-056 + dashboard panel move — Nests live in the sidebar
              now. Compact rows (code badge + name) are always visible
              like the original ctsk-056 shortcut design; hovering a
              single row expands it inline to show the rich stats
              (Rules / File wfs / Docs / Items). Click navigates to
              /documents?nest=<slug>. Hidden when the org has no nests
              so it's invisible in non-nest orgs.
              Section ordering: TOP_NAV_SECTIONS → Nests → BOTTOM_NAV_SECTIONS.
              Settings is in BOTTOM_NAV_SECTIONS so it always sits below
              the Nests block (per dr.the0 2026-05-30). */}
          {nests.length > 0 && (
            <div>
              <hr className="sep" />
              <div
                style={{
                  padding: '4px 20px',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--text-muted)',
                  marginTop: 4,
                }}
              >
                Nests
              </div>
              <div
                style={{
                  padding: '4px 12px 8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                {nests.map((n) => {
                  const stats = nestStatsBySlug.get(n.slug);
                  const sp = typeof window !== 'undefined'
                    ? new URLSearchParams(window.location.search)
                    : new URLSearchParams();
                  const active = sp.get('nest') === n.slug;
                  return (
                    <SidebarNestCard
                      key={n.id}
                      code={n.code}
                      name={n.name}
                      description={n.description ?? null}
                      active={active}
                      slug={n.slug}
                      onNavigate={onNavigate}
                      stats={stats}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Bottom nav sections — Settings sits here (below Nests)
              per dr.the0 2026-05-30. Always rendered, regardless of
              whether the org has nests, so the operator can always
              reach Settings from the same spot. */}
          {BOTTOM_NAV_SECTIONS.map((section) => (
            <NavSectionView
              key={section.heading}
              section={section}
              showSeparator
              currentPath={currentPath}
              onNavigate={onNavigate}
            />
          ))}
        </div>
        {/* Previous sidebar version strip lived here — replaced by the
            persistent <AppFooter /> pinned bottom-left (hyper-scanno
            DESIGN.md §5a). Version, build SHA, and org slug all live
            in the footer now; no need to duplicate in the sidebar. */}
      </nav>
      {/* Lifecycle banners (0.1.7). Two orthogonal states, rendered as
          thin chrome strips above the main content so the operator
          always knows when the active org isn't in its normal state.
          Both banners are advisory; the server enforces the write-lock
          via the global archive-guard preHandler (423 LOCKED). */}
      {session?.activeOrg?.archivedAt && (
        <div
          role="status"
          style={{
            background: 'color-mix(in srgb, var(--warning-primary) 14%, var(--surface-raised))',
            color: 'var(--warning-primary)',
            padding: '6px 16px',
            fontSize: 12,
            fontWeight: 500,
            borderBottom: '1px solid color-mix(in srgb, var(--warning-primary) 30%, transparent)',
          }}
        >
          📦 <strong>{session.activeOrg.name}</strong> is archived (read-only).
          Switch orgs to make changes, or unarchive from{' '}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); onNavigate('/settings?section=organizations'); }}
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >Settings → Organizations</a>.
        </div>
      )}
      {session?.activeOrg?.isHidden && !session?.activeOrg?.archivedAt && (
        <div
          role="status"
          style={{
            background: 'var(--surface-sunken)',
            color: 'var(--text-muted)',
            padding: '4px 16px',
            fontSize: 11,
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          👁 <strong>{session.activeOrg.name}</strong> is hidden from your default org listing
          (visible with "Show hidden" in the switcher).
        </div>
      )}
      <main className="shell__main">{children}</main>
      {/* Persistent app footer (hyper-scanno DESIGN.md §5a). Fixed
          bottom-left of the viewport; the shell layout reserves
          clearance via .shell__main padding-bottom + .shell__nav
          padding-bottom in globals.css. */}
      <AppFooter onNavigate={onNavigate} />
    </div>
  );
}

/**
 * Render one labeled nav section (uppercase heading + items + active
 * highlight). Shared between TOP_NAV_SECTIONS and BOTTOM_NAV_SECTIONS
 * so the System group below Nests looks identical to the groups above.
 */
function NavSectionView({
  section,
  showSeparator,
  currentPath,
  onNavigate,
}: {
  section: NavSection;
  showSeparator: boolean;
  currentPath: string;
  onNavigate: (path: string) => void;
}) {
  return (
    <div>
      {showSeparator && <hr className="sep" />}
      <div
        style={{
          padding: '4px 20px',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-muted)',
          marginTop: showSeparator ? 4 : 0,
        }}
      >
        {section.heading}
      </div>
      {section.items.map((item) => {
        const active = item.path === '/'
          ? currentPath === '/'
          : currentPath === item.path || currentPath.startsWith(item.path + '/');
        return (
          <Fragment key={item.path}>
            {item.groupBreakBefore && (
              <hr className="sep" role="separator" aria-orientation="horizontal" data-testid="nav-group-separator" />
            )}
            <a
              href={item.path}
              className={active ? 'active' : undefined}
              onClick={(e) => { e.preventDefault(); onNavigate(item.path); }}
            >
              {item.label}
            </a>
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * Sidebar per-nest row. Default state mirrors the original ctsk-056
 * shortcut shape: code badge + name on one line, always visible.
 * Hovering (or keyboard-focusing anywhere inside) the row expands it
 * inline to show the rich stats grid (Rules / File wfs / Docs /
 * Items). Each piece is independently clickable:
 *
 *   - Name row    → /documents?nest=<slug>   (the long-standing default)
 *   - Rules stat  → /tags/rules?nest=<slug>
 *   - File wfs    → /file-workflows?nest=<slug>
 *   - Docs        → /documents?nest=<slug>
 *   - Items       → /workitems?nest=<slug>
 *
 * Outer container is a div (not an anchor) so we can legally nest
 * multiple clickable children. Stats are optional so the row stays
 * usable while /stats/nests is still in flight.
 */
function SidebarNestCard({
  code,
  name,
  description,
  active,
  slug,
  onNavigate,
  stats,
}: {
  code: string;
  name: string;
  description: string | null;
  active: boolean;
  slug: string;
  onNavigate: (p: string) => void;
  stats?: {
    // CTSK-384 — bundle-declared landing destination for the name-row
    // click; wins over nestTileDocsPath's hardcoded routing.
    landingPath?: string;
    rulesCount: number;
    fileWorkflowsCount: number;
    documentsCount: number;
    workItemsCount: number;
    // 0.1.8 — installed tools count + per-tool metadata. Both
    // optional so older /stats/nests responses (or in-flight
    // queries) don't break the row.
    toolsCount?: number;
    tools?: Array<{ id: string; slug: string; type: string }>;
  };
}) {
  const [hovered, setHovered] = useState(false);
  const expanded = hovered;
  // 0.1.53 — CTSK-166 Phase B: when a nest slug is in NATIVE_NEST_TYPES,
  // the name row deep-links to the native page instead of the generic
  // documents-by-nest filter. Mirrors the Smart Tools deep-link pattern
  // (0.1.8) at the nest layer.
  // CTSK-357 — routing conditional extracted to nestTileDocsPath()
  // (lib/native-nest-types.ts) so it's unit-testable: ECN lands on its
  // native page with ?tab=pending (dr.the0 2026-08-17, was ?tab=all —
  // pending is the queue that needs action); other nests keep the
  // documents filter.
  // CTSK-384 — a bundle-declared nest.landingPath (rides /stats/nests)
  // now takes precedence over that hardcoded routing.
  const docsPath = nestTileDocsPath(slug, stats?.landingPath);
  const rulesPath = `/tags/rules?nest=${encodeURIComponent(slug)}`;
  const workflowsPath = `/file-workflows?nest=${encodeURIComponent(slug)}`;
  const itemsPath = `/workitems?nest=${encodeURIComponent(slug)}`;
  // 0.1.8 — Smart Tools deep-link. Precedence order (CTSK-344 EPIC;
  // documented per CTSK-348, implemented via resolveToolLandingUrl
  // since CTSK-347):
  //   1. tool.manifest.landingPath (CTSK-347) — a single tool that
  //      declares a landingPath wins over everything below. Relative
  //      routes navigate in-app; absolute http(s) URLs open in a new
  //      tab. Rides /stats/nests per-tool metadata.
  //   2. 1 tool with a known native type → route directly to the
  //      tool's native page (the common case for the data-sources-
  //      registry — clicking the cell takes you straight to the
  //      dashboard, not via Settings).
  //   3. 2+ tools, or 1 tool without a native page → Settings →
  //      Tools filtered by this nest via &nestSlug= (operator picks
  //      which to open; filter consumed by ToolsPanel since CTSK-348,
  //      routed since CTSK-346).
  //   4. 0 tools installed → same Settings → Tools URL (the &nestSlug=
  //      filter shows the nest's empty state) so the operator can
  //      install one.
  const tools = stats?.tools ?? [];
  const toolsPath = tools.length === 1
    ? resolveToolLandingUrl(tools[0]!, { slug })
    : defaultToolsSettingsPath(slug);
  const toolsExternal = isExternalToolUrl(toolsPath);
  return (
    <div
      // focus-within-style behavior — keep the row expanded while
      // ANY child has focus (so keyboard nav between stat cells
      // doesn't collapse the panel mid-tab).
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setHovered(false);
        }
      }}
      title={`${name}${description ? ` — ${description}` : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: expanded ? 6 : 0,
        padding: expanded ? 8 : '4px 8px',
        borderRadius: 'var(--radius-md)',
        background: active
          ? 'color-mix(in srgb, var(--accent-primary) 14%, transparent)'
          : expanded
          ? 'var(--surface-raised)'
          : 'transparent',
        border: expanded || active ? '1px solid var(--border-subtle)' : '1px solid transparent',
        transition: 'background 120ms ease, padding 120ms ease, border-color 120ms ease',
      }}
    >
      {/* Name row — clickable, routes to /documents?nest=<slug>
          (the original shortcut behavior). Inline anchor so we don't
          need to manage focus styling separately.
          CTSK-354 — the row also carries a Copy-short-URL button
          (/nt/:slug) beside the anchor; the anchor keeps flex:1 so
          long names still ellipsize. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <a
        href={docsPath}
        className={active ? 'active' : undefined}
        onClick={(e) => { e.preventDefault(); onNavigate(docsPath); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minHeight: 18,
          textDecoration: 'none',
          color: 'inherit',
          padding: 0,
          background: 'transparent',
          flex: 1,
          minWidth: 0,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 26,
            padding: '1px 5px',
            background: active ? 'var(--accent-primary)' : 'color-mix(in srgb, var(--accent-primary) 14%, transparent)',
            color: active ? '#fff' : 'var(--accent-primary)',
            borderRadius: 3,
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            letterSpacing: '.04em',
          }}
        >
          {code}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
        >
          {name}
        </span>
      </a>
      <CopyShortUrlButton
        shortPath={`/nt/${slug}`}
        label={name}
        size={12}
        style={{ padding: 2 }}
      />
      </div>
      {/* Hover-reveal stats grid. Each cell is its own deep-link. */}
      <div
        aria-hidden={!expanded}
        style={{
          // Bumped max-height from 120 to 160 to fit the third row
          // (5 cells in a 2-col grid = 3 rows; the third row only
          // has 1 cell but the container still needs height for it).
          maxHeight: expanded && stats ? 160 : 0,
          opacity: expanded && stats ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 160ms ease, opacity 120ms ease',
        }}
      >
        {stats && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 4,
              paddingTop: 4,
            }}
          >
            <SidebarStat
              label="Rules"
              value={stats.rulesCount}
              path={rulesPath}
              onNavigate={onNavigate}
            />
            <SidebarStat
              label="File wfs"
              value={stats.fileWorkflowsCount}
              path={workflowsPath}
              onNavigate={onNavigate}
            />
            <SidebarStat
              label="Docs"
              value={stats.documentsCount}
              path={docsPath}
              onNavigate={onNavigate}
            />
            <SidebarStat
              label="Items"
              value={stats.workItemsCount}
              path={itemsPath}
              onNavigate={onNavigate}
            />
            {/* 0.1.8 — Tools cell. Spans both columns on the third
                row so the layout doesn't end with a lone half-width
                cell. Deep-links to Settings → Tools filtered to
                this nest. */}
            <SidebarStat
              label="Tools"
              value={stats.toolsCount ?? 0}
              path={toolsPath}
              onNavigate={onNavigate}
              spanFull
              // CTSK-347 — absolute landingPath opens in a new tab.
              external={toolsExternal}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SidebarStat({
  label,
  value,
  path,
  onNavigate,
  spanFull,
  external,
}: {
  label: string;
  value: number;
  path: string;
  onNavigate: (p: string) => void;
  // 0.1.8 — when true, the cell spans both columns of the 2-col
  // grid. Used for the Tools cell so the third row doesn't end with
  // a lonely half-width cell.
  spanFull?: boolean;
  // CTSK-347 — when true, `path` is an absolute http(s) URL (a
  // manifest landingPath): let the anchor do its native thing in a
  // new tab instead of hijacking the click into in-app onNavigate.
  external?: boolean;
}) {
  return (
    <a
      href={path}
      {...(external
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : { onClick: (e: ReactMouseEvent) => { e.preventDefault(); onNavigate(path); } })}
      title={`${label} in this nest — open${external ? ' (new tab)' : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        padding: '3px 6px',
        background: 'var(--surface-sunken)',
        borderRadius: 3,
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: 'background 100ms ease',
        ...(spanFull ? { gridColumn: '1 / -1' } : {}),
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.background =
          'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-sunken))';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.background = 'var(--surface-sunken)';
      }}
    >
      <span
        style={{
          fontSize: 9,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '.04em',
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </a>
  );
}
